/**
 * Directory data loader. Added for the FE↔BE integration (Round 1).
 *
 * Fetches the connector catalog, the tenant's connections, and the tenant's
 * grants from the backend, and maps them into the FE's existing shapes:
 *  - integrations → the built-in INTS list (replaced in place at boot)
 *  - connections  → state.conn  (keyed by connector slug)
 *  - grants       → state.routes (keyed "product:dataType" as the FE expects)
 *
 * Keeping the BE→FE translation here means the directory components, the
 * `allInts` merge, and the reducer all stay untouched.
 */
import { apiFetch } from './client';
import {
  toFeIntegration,
  type BeIntegration,
  type CanonicalRoute,
  mapBinding,
} from './mappers';
import { INTS } from '../data/seeds';
import type { AuthMethod, Conn, Integration, ProductBinding } from '../domain/types';
import type { AuthCfg } from '../domain/auth';

/**
 * Snapshot the FE seed's product bindings (slug → bindings) BEFORE INTS is
 * mutated by applyBackendIntegrations. The backend models eligibility by owner
 * code, not the FE's display bindings, so the directory keeps using the FE seed
 * bindings for the grant drawer; this preserves them across the swap.
 */
export function seedBindings(): Record<string, ProductBinding[]> {
  const map: Record<string, ProductBinding[]> = {};
  for (const i of INTS) map[i.id] = i.products;
  // Backend-only connectors (not in the FE INTS seed) need explicit bindings so
  // their grant drawer shows toggles. zoho-crm serves CRM contacts → crm/Party.
  // (Without this the grant drawer renders empty for an MCP-connected Zoho.)
  if (!map['zoho-crm']) {
    map['zoho-crm'] = [{ key: 'CRM', dataType: 'contacts' }];
  }
  return map;
}

interface BeConnection {
  connection_id: string;
  integration_id: string;
  vendor_version: string;
  auth_method: string;
  status: string; // active | revoked | expired
  connected_at: string;
}

interface BeGrant {
  grant_id: string;
  product_key: string; // canonical owner code (cal/crm/...)
  data_type: string; // canonical entity key (Engagement/Party/...)
  connection_id: string;
  is_active: boolean;
}

export interface DirectoryData {
  integrations: Integration[];
  conn: Record<string, Conn>;
  routes: Record<string, string>;
  // connection_id ↔ slug lookups, needed by later rounds (connect/grant).
  connBySlug: Record<string, string>;
  slugById: Record<string, string>;
  // Per-connector auth config seeded from the BE's allowed/recommended methods.
  // WHY: the BE rejects a connect whose auth_method isn't in allowed_auth_methods
  // (e.g. Stripe = apikey/bearer only). Seeding authCfg here makes the FE connect
  // modal offer exactly the methods the backend will accept (BE canonical).
  authCfg: Record<string, AuthCfg>;
  // FE route key ("product:dataType") → BE grant_id. Needed to revoke a grant,
  // since the FE keys routes by binding but the BE deletes by grant_id.
  grantIdByRoute: Record<string, string>;
}

/**
 * Load the directory from the backend. `internal` controls whether we send the
 * Softknack admin header (the /integrations list is an internal-only route, so
 * the directory always loads it with internal=true; tenant-scoped reads still
 * resolve the same tenant).
 */
export async function loadDirectory(seedBindings: Record<string, ProductBinding[]>): Promise<DirectoryData> {
  // /integrations is Softknack-internal — always request it with the admin
  // header so the directory can show the full catalog regardless of FE role.
  const ints = await apiFetch<{ integrations: BeIntegration[] }>('/integrations', { internal: true });
  // Connections + grants are tenant-scoped (no internal header needed).
  const conns = await apiFetch<{ connections: BeConnection[] }>('/connections');
  const grants = await apiFetch<{ grants: BeGrant[] }>('/grants');

  // integration_id → slug, so connections/grants (which reference integration_id
  // / connection_id) can be re-keyed to the slug the FE uses everywhere.
  const slugById: Record<string, string> = {};
  for (const i of ints.integrations) slugById[i.integration_id] = i.slug;

  // Map integrations into FE shape. Product bindings come from the FE seed
  // (the BE models eligibility by owner code, not the FE's display bindings),
  // falling back to empty if a backend-only connector has no seed entry.
  const integrations: Integration[] = ints.integrations.map((be) =>
    toFeIntegration(be, seedBindings[be.slug] || []),
  );

  // authCfg keyed by slug, from the BE's allowed/recommended methods. Only set
  // methods we recognize; an empty allowed list leaves the entry off so the FE
  // falls back to its class defaults rather than offering zero methods.
  const authCfg: Record<string, AuthCfg> = {};
  for (const be of ints.integrations) {
    const methods = (be.allowed_auth_methods || []).filter(isAuthMethod);
    if (methods.length === 0) continue;
    const cfg: AuthCfg = { methods };
    if (be.recommended_auth_method && isAuthMethod(be.recommended_auth_method)) {
      cfg.recommended = be.recommended_auth_method;
    }
    authCfg[be.slug] = cfg;
  }

  // Connections → state.conn, keyed by slug. A connector with an active/expired/
  // revoked connection drives its directory status pill.
  const conn: Record<string, Conn> = {};
  const connBySlug: Record<string, string> = {};
  for (const c of conns.connections) {
    const slug = slugById[c.integration_id];
    if (!slug) continue; // connection for an integration not in the list — skip
    connBySlug[slug] = c.connection_id;
    conn[slug] = {
      connected: c.status === 'active' || c.status === 'expired', // revoked = not usable
      status: c.status as Conn['status'],
      vendorVersion: c.vendor_version,
    };
  }

  // Grants → state.routes. The FE keys routes "product:dataType" using its own
  // vocabulary, so we translate the canonical (product_key, data_type) back to
  // the FE binding via the same mapping table used outbound. Only the bindings
  // catalog v1 can represent appear here; the rest were never grantable.
  const routes: Record<string, string> = {};
  const grantIdByRoute: Record<string, string> = {};
  const canonicalToFeKey = buildCanonicalReverseIndex(seedBindings);
  for (const g of grants.grants) {
    if (!g.is_active) continue;
    // A grant references a connection, which references an integration → slug.
    const slug = resolveGrantSlug(g, conns.connections, slugById);
    const feKey = canonicalToFeKey[g.product_key + ':' + g.data_type];
    if (feKey && slug) {
      routes[feKey] = slug;
      grantIdByRoute[feKey] = g.grant_id; // so the FE can revoke this route later
    }
  }

  return { integrations, conn, routes, connBySlug, slugById, authCfg, grantIdByRoute };
}

// Narrow a BE auth-method string to the FE AuthMethod union.
function isAuthMethod(m: string): m is AuthMethod {
  return m === 'oauth' || m === 'apikey' || m === 'bearer';
}

// Resolve which connector slug a grant belongs to, via its connection.
function resolveGrantSlug(
  g: BeGrant,
  connections: BeConnection[],
  slugById: Record<string, string>,
): string | undefined {
  const c = connections.find((x) => x.connection_id === g.connection_id);
  return c ? slugById[c.integration_id] : undefined;
}

// Build a reverse lookup "canonicalProduct:canonicalEntity" → "FEproduct:FEdataType"
// from the FE seed bindings, so grants coming back from the BE can be re-keyed to
// the exact FE route key the grant drawer renders.
function buildCanonicalReverseIndex(
  seedBindings: Record<string, ProductBinding[]>,
): Record<string, string> {
  const index: Record<string, string> = {};
  for (const bindings of Object.values(seedBindings)) {
    for (const b of bindings) {
      const canonical: CanonicalRoute | null = mapBinding(b);
      if (canonical) {
        index[canonical.productKey + ':' + canonical.dataType] = b.key + ':' + b.dataType;
      }
    }
  }
  return index;
}

/**
 * Re-fetch just the tenant-scoped connection/grant state and return the store
 * patch. Used after a connect/revoke/grant mutation to refresh the directory
 * without reloading the whole page. Reuses loadDirectory but only the
 * conn/routes/connBySlug parts matter here (integrations rarely change).
 */
export async function refreshConnState(
  seedBindings: Record<string, ProductBinding[]>,
): Promise<Pick<DirectoryData, 'conn' | 'routes' | 'connBySlug' | 'slugById' | 'grantIdByRoute'>> {
  const data = await loadDirectory(seedBindings);
  return {
    conn: data.conn,
    routes: data.routes,
    connBySlug: data.connBySlug,
    slugById: data.slugById,
    grantIdByRoute: data.grantIdByRoute,
  };
}

/**
 * Replace the contents of the built-in INTS array in place with the backend's
 * integrations. WHY in-place mutation: INTS is imported (by value) in several
 * modules (catalog merge, versions, store). Mutating the array contents updates
 * all of them at once without changing any import or function signature. When
 * USE_BACKEND is false this is never called, so the seed data stands.
 */
export function applyBackendIntegrations(integrations: Integration[]): void {
  INTS.splice(0, INTS.length, ...integrations);
}
